import * as React from 'react'
import clsx from 'clsx'

import { Button } from '@/components/button/Button'
import { ModalContainer } from './ModalContainer'
import { ModalHeader } from './ModalHeader'

export type TabbedModalTab = {
  /** Unique identifier for this tab. */
  id: string
  /** Label for this tab. */
  label: string
  /** Optional decorative icon displayed before the label. */
  leadingIcon?: React.ReactNode
  /** Optional accessible label when the visible label does not convey the complete tab state. */
  ariaLabel?: string
  /** Body for this tab; rendered full width (no modal horizontal padding). */
  panel: React.ReactNode
}

export type TabbedModalProps = Omit<React.ComponentPropsWithoutRef<typeof ModalContainer>, 'children'> & {
  /** Title in the header above the tab strip. */
  title?: string
  /** Alignment of the title. Default: 'left' */
  titleAlign?: 'left' | 'center'
  /** Icon displayed before the title text. */
  titleIcon?: React.ReactNode
  /** Optional subtitle below the title. */
  subtitle?: string
  /** Show the close button in the header. Default: false */
  showCloseButton?: boolean
  /** Footer below the tab panels (e.g. primary/secondary actions). */
  footer?: React.ReactNode
  /** Tab definitions (id, label, panel content). */
  tabs: TabbedModalTab[]
  /** Selected tab id; must match a `tabs` entry. */
  activeTabId: string
  /** Called when the user switches tabs. */
  onTabChange: (tabId: string) => void
  /** Trailing control in the tab strip (e.g. Change Matter). */
  tabBarTrailing?: React.ReactNode
  /** Control on the title row, filling the space after the title (e.g. a search field). */
  titleTrailing?: React.ReactNode
  /**
   * Hide the tab strip while still rendering the active tab's panel. For a mode that takes over the
   * whole body — such as search results spanning every tab's source — so the modal stays mounted
   * and focus is preserved instead of swapping to a different layout. Default: false
   */
  hideTabBar?: boolean
}

/**
 * Tabbed dialog layout built on {@link ModalContainer}.
 * Render as a child of {@link Modal} (Radix `Dialog.Root`).
 */
export function TabbedModal({
  title,
  titleAlign = 'left',
  titleIcon,
  subtitle,
  width = 'md',
  height = 'md',
  preventDismiss = false,
  showCloseButton = false,
  stacked = false,
  footer,
  tabs,
  activeTabId,
  onTabChange,
  tabBarTrailing,
  titleTrailing,
  hideTabBar = false,
  ...props
}: TabbedModalProps) {
  const reactId = React.useId()
  const activeTab = tabs.find((t) => t.id === activeTabId) ?? tabs[0]
  const tabButtonRefs = React.useRef<Map<string, HTMLButtonElement>>(new Map())

  const handleTabKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || tabs.length === 0) return

      if (tabs.length === 1) {
        event.preventDefault()
        return
      }

      event.preventDefault()

      let nextIndex: number
      switch (event.key) {
        case 'ArrowRight':
          nextIndex = (index + 1) % tabs.length
          break
        case 'ArrowLeft':
          nextIndex = (index - 1 + tabs.length) % tabs.length
          break
        case 'Home':
          nextIndex = 0
          break
        case 'End':
          nextIndex = tabs.length - 1
          break
        default:
          return
      }

      const next = tabs[nextIndex]
      onTabChange(next.id)
      tabButtonRefs.current.get(next.id)?.focus()
    },
    [tabs, onTabChange]
  )

  const hasHeader = Boolean(title || subtitle || showCloseButton || titleTrailing)

  return (
    <ModalContainer
      {...props}
      {...(!subtitle && { 'aria-describedby': undefined })}
      data-ds-component="TabbedModal"
      width={width}
      height={height}
      stacked={stacked}
      preventDismiss={preventDismiss}
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col pt-6">
        {hasHeader ? (
          <div className="shrink-0 px-6 pb-4">
            <ModalHeader
              showCloseButton={showCloseButton}
              subtitle={subtitle}
              title={title}
              titleAlign={titleAlign}
              titleIcon={titleIcon}
              titleTrailing={titleTrailing}
            />
          </div>
        ) : null}

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-visible border-t border-border-default">
          {hideTabBar ? null : (
            <div className="flex min-w-0 shrink-0 items-center justify-between gap-4 overflow-visible border-b border-border-default">
              <div className="flex min-w-0 items-center gap-8 overflow-visible py-1 pl-6" role="tablist" aria-label="Modal tabs" aria-orientation="horizontal">
                {tabs.map((tab, index) => {
                  const selected = tab.id === activeTabId

                  return (
                    <div
                      key={tab.id}
                      className={clsx(
                        '-mb-px inline-flex h-9 flex-col justify-center border-b-2',
                        selected ? 'relative z-[1] border-brand-800' : 'border-transparent'
                      )}
                    >
                      <Button
                        ref={(el) => {
                          const m = tabButtonRefs.current
                          if (el) m.set(tab.id, el)
                          else m.delete(tab.id)
                        }}
                        type="button"
                        variant="plain"
                        color="neutral"
                        size="sm"
                        role="tab"
                        tabIndex={selected ? 0 : -1}
                        id={`${reactId}-${tab.id}-tab`}
                        aria-selected={selected}
                        aria-controls={`${reactId}-${tab.id}-panel`}
                        aria-label={tab.ariaLabel}
                        onClick={() => onTabChange(tab.id)}
                        onKeyDown={(e) => handleTabKeyDown(e, index)}
                        leadingIcon={tab.leadingIcon}
                        className="aria-selected:text-brand-800"
                      >
                        {tab.label}
                      </Button>
                    </div>
                  )
                })}
              </div>
              {tabBarTrailing ? <div className="shrink-0 pr-6">{tabBarTrailing}</div> : null}
            </div>
          )}

          <div className="min-h-0 min-w-0 flex-1 overflow-hidden border-b border-border-default">
            {activeTab ? (
              // Without the strip there is no tab to label the panel, so it is a plain region
              // rather than a `tabpanel` pointing at a button that is not rendered.
              <div
                key={activeTab.id}
                id={hideTabBar ? undefined : `${reactId}-${activeTab.id}-panel`}
                role={hideTabBar ? undefined : 'tabpanel'}
                aria-labelledby={hideTabBar ? undefined : `${reactId}-${activeTab.id}-tab`}
                className="flex h-full min-h-0 flex-col"
              >
                {activeTab.panel}
              </div>
            ) : null}
          </div>
        </div>

        {footer ? <div className="mt-auto shrink-0 px-6 py-4">{footer}</div> : null}
      </div>
    </ModalContainer>
  )
}
